import type { SupCat } from '@itc-mod/supply/shared';

// Sierra's op templates and limits (plan 2D Assumption 1 and R4, after `ACQ_TYPES`
// and `OUTCOMES`, sierra-panel.html lines 416–451).

export const ACQ_TYPE_IDS = ['mining', 'salvage', 'wpn_space', 'wpn_ground', 'wpn_both', 'other'] as const;
export type AcqTypeId = (typeof ACQ_TYPE_IDS)[number];

/** How an op ended. A blank outcome means it hasn't been judged. */
export const OUTCOMES = ['Success', 'Partial success', 'Failed', 'Scrubbed'] as const;

/** The most an op holds. 80 crew fits duplicating in one batch of 100. RSVP, voice and Fleet rows don't count toward it, and have their own limits. */
export const LIMITS = { checklist: 60, timeline: 30, loadout: 30, ships: 20, crew: 80, rsvp: 200, voice: 200, fleet: 200, haul: 200, sops: 20, reports: 30 } as const;

/** What a report holds (plan 3C R1). Participants are all of an op's crew, voice and Fleet rows. */
export const REPORT_LIMITS = { screenshots: 10, caption: 200, title: 200, heading: 80, text: 20_000, sections: 12, participants: LIMITS.crew + LIMITS.voice + LIMITS.fleet, preview: 300, hiddenReason: 180 } as const;

/** A voice window nobody closed closes itself this long after opening (plan 3B). */
export const VOICE_LIMIT_MS = 12 * 60 * 60 * 1000;

/** How long after closing a window settles, so a reconnecting worker's late batch still counts (plan 3B Assumption 4). */
export const VOICE_GRACE_MS = 5 * 60 * 1000;

/** An op stays Upcoming until this long after its start (lines 666, 948). */
export const UPCOMING_GRACE_MS = 6 * 60 * 60 * 1000;

/** How far a duplicate's times move (`dupOp`, line 1256). */
export const DUPLICATE_SHIFT_MS = 7 * 24 * 60 * 60 * 1000;

/** How long Recent is on the Schedule (line 1272). */
export const RECENT_LIMIT = 8;

export interface AcqTemplate {
  label: string;
  short: string;
  /** Supply's category for the template's haul. */
  category: SupCat;
  unit: 'scu' | 'units';
  objective: string;
  /** The briefing outline. */
  plan: string;
  checklist: readonly string[];
  /** Timeline entries: minutes from the start, and what happens. */
  phases: readonly (readonly [number, string])[];
  /** Haul quick picks. */
  picks: readonly string[];
}

export const ACQ_TYPES: Readonly<Record<AcqTypeId, AcqTemplate>> = {
  mining: {
    label: 'Mining',
    short: 'Mining',
    category: 'Materials',
    unit: 'scu',
    objective: 'Mine and refine ore for the Sierra stockpile.',
    plan: 'Field: \nTarget ores: \nMinimum rock quality: \nRefinery: \nHauler: \nEscort: ',
    checklist: ['Mining heads and modules fitted', 'Gadgets loaded', 'Target field and ores picked', 'Refinery picked', 'Hauler assigned for ore', 'Escort assigned', 'Fuel and quantum topped off', 'Comms channel posted'],
    phases: [
      [-30, 'Muster and form up'],
      [0, 'Depart for the field'],
      [150, 'Last rock, head for the refinery'],
      [180, 'Ore dropped off, refinery job logged'],
    ],
    picks: ['Quantanium', 'Laranite', 'Agricium', 'Bexalite', 'Taranite', 'Gold', 'Hephaestanite', 'Borase', 'Titanium', 'Tungsten', 'Copper', 'Iron'],
  },
  salvage: {
    label: 'Salvage',
    short: 'Salvage',
    category: 'Commodities',
    unit: 'scu',
    objective: 'Strip hulls for RMC and construction materials for the Sierra stockpile.',
    plan: 'Salvage site or contract: \nSalvage ships: \nHauler: \nDrop-off: \nEscort: ',
    checklist: ['Salvage heads fitted, spare filters aboard', 'Tractor beams and multitools', 'Wrecks or salvage contract lined up', 'Hauler for RMC and CM boxes', 'Escort assigned', 'Drop-off point picked', 'Fuel and quantum topped off', 'Comms channel posted'],
    phases: [
      [-30, 'Muster and form up'],
      [0, 'Depart for the salvage site'],
      [150, 'Final scrape, load out'],
      [180, 'Unload at drop-off'],
    ],
    picks: ['RMC', 'Construction Materials', 'Ship components', 'Ship weapons'],
  },
  wpn_space: {
    label: 'Weapon acquisition – Space',
    short: 'Weapons · Space',
    category: 'Weapons & gear',
    unit: 'units',
    objective: 'Recover ship weapons, components and ordnance for the Sierra stockpile.',
    plan: 'Target: \nShips: \nCargo space for recovered items: \nRTB point: ',
    checklist: ['Ships armed and restocked', 'Missiles loaded', 'Cargo space set aside for recovered items', 'Tractor beam aboard', 'Medical and respawn plan', 'Comms channel posted'],
    phases: [
      [-30, 'Muster and form up'],
      [0, 'Launch'],
      [120, 'Collect recovered items'],
      [150, 'RTB and unload'],
    ],
    picks: ['Ship weapons', 'Ship components', 'Missiles', 'Torpedoes'],
  },
  wpn_ground: {
    label: 'Weapon acquisition – Ground',
    short: 'Weapons · Ground',
    category: 'Weapons & gear',
    unit: 'units',
    objective: 'Recover personal weapons, armor and gear for the Sierra stockpile.',
    plan: 'Target: \nTransport: \nLoot carriers: \nRally / extraction: ',
    checklist: ['Kits issued', 'Medical team assigned', 'Transport ship confirmed', 'Empty backpacks for loot', 'Respawn point set', 'Rally and extraction points set', 'Comms channel posted'],
    phases: [
      [-30, 'Muster and kit up'],
      [0, 'Step off'],
      [120, 'Sweep for loot'],
      [150, 'Extract and stash'],
    ],
    picks: ['Railgun', 'Rifles', 'LMGs', 'Armor sets', 'Medical gear', 'Grenades'],
  },
  wpn_both: {
    label: 'Weapon acquisition – Space + Ground',
    short: 'Weapons · Both',
    category: 'Weapons & gear',
    unit: 'units',
    objective: 'Recover ship and personal weapons for the Sierra stockpile.',
    plan: 'Target: \nHub ship: \nGround teams: \nShip cover: \nRally / extraction: ',
    checklist: ['Hub ship restocked and parked at staging', 'Ships armed and restocked', 'Kits issued', 'Medical team assigned', 'Respawn point set on hub ship', 'Empty backpacks and cargo space for loot', 'Rally and extraction points set', 'Comms channel posted'],
    phases: [
      [-30, 'Muster at hub ship'],
      [0, 'Launch and step off'],
      [120, 'Sweep for loot'],
      [150, 'Extract and unload at hub'],
    ],
    picks: ['Railgun', 'Rifles', 'Armor sets', 'Ship weapons', 'Ship components', 'Missiles'],
  },
  other: {
    label: 'Other acquisition',
    short: 'Acquisition',
    category: 'Other',
    unit: 'units',
    objective: '',
    plan: '',
    checklist: [],
    phases: [
      [-30, 'Muster'],
      [0, 'Step off'],
    ],
    picks: [],
  },
};
